import { createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import {
  showError,
  showSuccess,
} from "../../helpers/Notifications/Notification";
import api from "../../helpers/axios";
import {
  ORGANISATION_PROFILE,
  GET_STATES_DROPDOWN_DATA,
  GET_SECTORS_DROPDOWN_DATA,
  GET_ORG_TYPES,
} from "../../helpers/url_helpers";
import { clearOverlayCache } from "../../pages/IncidentResponse/api/overlaysApi";

const initialState = {
  loading: false,
  updating: false,
  organisationProfileData: "",
  organisationProfileEditResponse: "",
  statesDropdownData: [],
  sectorsDropdownData: [],
  dropdownDataFetchingError: "",
  organisationProfileError: "",
  orgTypeMasterData: [],
};

const organisationProfile = createSlice({
  name: "organisationProfile",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload.loading;
    },
    setUpdating: (state, action) => {
      state.updating = action.payload;
    },
    setOrganisationProfileData: (state, action) => {
      state.organisationProfileData = action.payload.organisationProfileData;
    },
    setOrganisationProfileEditResponse: (state, action) => {
      state.organisationProfileEditResponse =
        action.payload.organisationProfileEditResponse;
    },
    setStatesDropdownData: (state, action) => {
      state.statesDropdownData = action.payload.statesDropdownData;
    },
    setSectorsDropdownData: (state, action) => {
      state.sectorsDropdownData = action.payload.sectorsDropdownData;
    },
    setOrganisationTypes: (state, action) => {
      state.orgTypeMasterData = action.payload.orgTypeMasterData;
    },
    setError: (state, action) => {
      state.organisationProfileError = action.payload.organisationProfileError;
    },
    setDropdownFetchError: (state, action) => {
      state.dropdownDataFetchingError =
        action.payload.dropdownDataFetchingError;
    },
  },
});

export const {
  setLoading,
  setOrganisationProfileData,
  setOrganisationProfileEditResponse,
  setStatesDropdownData,
  setSectorsDropdownData,
  setOrganisationTypes,
  setError,
  setUpdating,
  setDropdownFetchError,
} = organisationProfile.actions;

export default organisationProfile.reducer;

export const getOrganisation = () => async (dispatch) => {
  try {
    dispatch(setLoading({ loading: true }));
    dispatch(setOrganisationProfileData({ organisationProfileData: "" }));
    // GET organisation api call
    const response = await api.get(ORGANISATION_PROFILE);
    if (response.data.error) {
      dispatch(
        setError({
          organisationProfileError: response.data.error,
        })
      );
    } else {
      dispatch(
        setOrganisationProfileData({
          organisationProfileData: response?.data?.data,
        })
      );
    }
    dispatch(setLoading({ loading: false }));
  } catch (error) {
    dispatch(
      setError({
        organisationProfileError: error,
      })
    );
    dispatch(setLoading({ loading: false }));
  }
};

export const getOrganizerStateDropdownData = () => async (dispatch) => {
  try {
    dispatch(setLoading({ loading: true }));
    dispatch(setStatesDropdownData({ statesDropdownData: [] }));
    dispatch(setSectorsDropdownData({ sectorsDropdownData: [] }));
    // GET MASTER DATA api call
    const statesResponse = await api.get(GET_STATES_DROPDOWN_DATA);
    if (statesResponse.data.error) {
      dispatch(
        setDropdownFetchError({
          dropdownDataFetchingError: statesResponse.data.error,
        })
      );
    } else {
      dispatch(
        setStatesDropdownData({
          statesDropdownData: statesResponse?.data?.data,
        })
      );
    }

    const sectorsResponse = await api.get(GET_SECTORS_DROPDOWN_DATA);

    if (sectorsResponse.data.error) {
      dispatch(
        setDropdownFetchError({
          dropdownDataFetchingError: sectorsResponse.data.error,
        })
      );
    } else {
      dispatch(
        setSectorsDropdownData({
          sectorsDropdownData: sectorsResponse?.data?.data,
        })
      );
    }
    dispatch(setLoading({ loading: false }));
  } catch (error) {
    dispatch(
      setDropdownFetchError({
        dropdownDataFetchingError: error,
      })
    );
    dispatch(setLoading({ loading: false }));
  }
};

// NOT COMPLETE
export const updateOrganisation = (payload, callback) => async (dispatch) => {
  try {
    dispatch(setUpdating(true));
    const formData = new FormData();
    formData.append("logo_file", payload.logo);
    formData.append("name", payload.organization_name);
    formData.append("facilitator_phone", payload.facilitator_phone);
    formData.append("date_of_CRR", payload.date_of_CRR);
    formData.append("sector", payload.sector);
    formData.append("critical_service", payload.critical_service);
    formData.append("location_city", payload.location_city);
    formData.append("location_state", payload.location_state);
    formData.append("critical_POC_name", payload.critical_POC_name);
    formData.append("critical_POC_phone", payload.critical_POC_phone);
    formData.append("critical_POC_email", payload.critical_POC_email);

    // PATCH organisation api call
    const response = await api.patch(ORGANISATION_PROFILE, formData);

    if (response.data.error) {
      dispatch(
        setError({
          organisationProfileError: response.data.error,
        })
      );

      showError(
        response.data?.error?.detail ||
          response.data?.message ||
          "Failed to Update Organization Profile"
      );
    } else {
      // State drives IR overlay (Ohio→OTRP); drop stale generic cache.
      clearOverlayCache();
      dispatch(setOrganisationProfileData({ organisationProfileData: "" }));
      dispatch(
        setOrganisationProfileEditResponse({
          organisationProfileEditResponse: response?.data?.data,
        })
      );
      dispatch(
        setOrganisationProfileData({
          organisationProfileData: response?.data?.data,
        })
      );
      showSuccess("Organization Profile Updated Successfully");
      if (callback) callback();
    }
    dispatch(setUpdating(false));
  } catch (error) {
    dispatch(
      setError({
        organisationProfileError: error,
      })
    );
    dispatch(setUpdating(false));
    showError("Failed to Update Organization Profile");
  }
};

export const getOrganizationTypes = () => async (dispatch) => {
  try {
    dispatch(setOrganisationTypes({ orgTypeMasterData: "" }));
    const response = await api.get(GET_ORG_TYPES);
    if (response) {
      if (response?.data?.error) {
        dispatch(
          setError({
            organisationProfileError: response.data.error,
          })
        );
      } else {
        dispatch(
          setOrganisationTypes({
            orgTypeMasterData: response.data.data,
          })
        );
      }
    }
  } catch (error) {
    dispatch(
      setError({
        organisationProfileError: error,
      })
    );
  }
};

export const clearError = () => async (dispatch) => {
  dispatch(setError({ organisationProfileError: {} }));
};
